import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Plus, Play, Square, Trash2, Bot, CircleDot, ArrowLeftRight, Waves, Edit2 } from "lucide-react";

const BOT_TYPES = [
  { value: "mid_price_brush", label: "Mid Price Brush", desc: "Sell-priority orders at avg of Bid1 & Ask2" },
  { value: "natural_brush", label: "Natural Brush", desc: "Natural fluctuation between Bid1 and Ask1" },
  { value: "oscillation", label: "Oscillation Bot", desc: "Price curve oscillation within range %" },
  { value: "best_oscillation", label: "Best Oscillation", desc: "Organic charts + anti-dump protection" },
  { value: "range_oscillation", label: "Range Oscillation", desc: "Oscillate price between min/max price" },
  { value: "depth_spread", label: "Depth Spread", desc: "Orderbook depth at 0-x% from benchmark" },
  { value: "depth_near", label: "Depth Near", desc: "Orderbook depth at y%-z% from benchmark" },
  { value: "depth_remote", label: "Depth Remote", desc: "BUY-only orders parked in remote band (e.g., 20%–25% below price)" },
  { value: "gradual_lift", label: "Gradual Lift", desc: "BUY-only with anti-dump floor — organic upward charts" },
  { value: "push", label: "Push", desc: "BUY orders only - push price to target" },
  { value: "order_flash", label: "Order Flash", desc: "Place LIMIT order, hold briefly, cancel — flash depth illusion" },
  { value: "cross_account", label: "Cross-Account Trade", desc: "Match orders between 2 accounts (no self-trade)" },
];

const DEFAULT_CONFIGS = {
  mid_price_brush: { min_order_size: 1000, max_order_size: 2000, interval_seconds: 5, price_mode: "normal", distribution_type: "continuous" },
  natural_brush: { min_order_size: 1000, max_order_size: 2000, interval_seconds: 5, order_type: "random", price_mode: "normal", distribution_type: "continuous" },
  oscillation: { min_order_size: 1000, max_order_size: 2000, max_cost_usdt: 1000, total_time_minutes: 60, oscillate_range_pct: 2, oscillate_ratio_pct: 0.5, distribution_type: "continuous" },
  best_oscillation: { min_order_size: 1000, max_order_size: 2000, interval_min_seconds: 3, interval_max_seconds: 8, oscillation_range_pct: 2, anti_dump_protection_pct: 5, spread_pct: 0.2, dump_threshold_pct: 1.0, dump_pause_seconds: 180, min_price: 0, max_price: 0 },
  range_oscillation: { min_order_size: 1000, max_order_size: 2000, min_price: 0, max_price: 0, min_interval: 5, max_interval: 10, max_move_per_trade_pct: 0.3, consolidation_chance_pct: 15, distribution_type: "continuous" },
  depth_spread: { buy_min_order_size: 1000, buy_max_order_size: 2000, buy_sheet_count: 5, sell_min_order_size: 1000, sell_max_order_size: 2000, sell_sheet_count: 5, duration_seconds: 30, buy_spread_min_pct: 0, buy_spread_pct: 2, sell_spread_min_pct: 0, sell_spread_pct: 2, layer_step_pct: 0, max_spread_tolerance_pct: 0, dump_protection_pct: 0, dump_pause_seconds: 60, price_mode: "current", pricing_value: 0, sell_price_floor: 0, buy_price_ceiling: 0, order_type: "normal", distribution_type: "longtail" },
  depth_near: { buy_min_order_size: 1000, buy_max_order_size: 2000, buy_sheet_count: 5, sell_min_order_size: 1000, sell_max_order_size: 2000, sell_sheet_count: 5, duration_seconds: 30, buy_spread_min_pct: 0.5, buy_spread_max_pct: 2, sell_spread_min_pct: 0.5, sell_spread_max_pct: 2, price_mode: "current", pricing_value: 0, sell_price_floor: 0, buy_price_ceiling: 0, order_type: "normal", distribution_type: "longtail" },
  depth_remote: { buy_min_order_size: 1000, buy_max_order_size: 2000, sheet_count: 5, start_spread_pct: 20, end_spread_pct: 25, duration_seconds: 30, price_mode: "current", pricing_value: 0, buy_price_ceiling: 0 },
  gradual_lift: { target_price: 0, min_order_size: 1000, max_order_size: 2000, max_cost_usdt: 1000, total_time_minutes: 60, oscillate_range_pct: 1, consolidation_chance_pct: 10, order_type: "LIMIT", distribution_type: "continuous" },
  push: { target_price: 0, min_order_size: 1000, max_order_size: 2000, interval_seconds: 5, order_type: "LIMIT", max_duration_minutes: 60, max_budget_usdt: 0 },
  order_flash: { min_order_size: 1000, max_order_size: 2000, side: "both", spread_pct: 0.2, flash_duration_seconds: 2, interval_seconds: 3 },
  cross_account: { min_order_size: 10, max_order_size: 10, interval_seconds: 2, buy_order_type: "LIMIT", price_anchor: "ask", price_offset_pct: 0.01, target_volume_usdt: 0, max_wait_seconds: 30, poll_interval_seconds: 0.2 },
};

function ConfigFields({ botType, config, setConfig, keys, apiKeyId2, setApiKeyId2 }) {
  const up = (key, val) => setConfig((p) => ({ ...p, [key]: val }));

  const numField = (label, key, step = 1) => (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-wider">{label}</Label>
      <Input type="number" step={step} value={config[key] ?? ""} onChange={(e) => up(key, parseFloat(e.target.value) || 0)} className="bg-secondary/50 font-mono text-sm" data-testid={`config-${key}`} />
    </div>
  );

  const selectField = (label, key, options) => (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-wider">{label}</Label>
      <Select value={config[key] || options[0]?.value} onValueChange={(v) => up(key, v)}>
        <SelectTrigger className="bg-secondary/50" data-testid={`config-${key}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  // Shared order size range fields for non-depth bots
  const sizeRangeFields = () => (
    <>
      {numField("Min Order Size", "min_order_size")}
      {numField("Max Order Size", "max_order_size")}
    </>
  );

  // Depth bot buy/sell size range fields
  const depthSizeFields = () => (
    <>
      {numField("Buy Min Size", "buy_min_order_size")}
      {numField("Buy Max Size", "buy_max_order_size")}
      {numField("Sell Min Size", "sell_min_order_size")}
      {numField("Sell Max Size", "sell_max_order_size")}
    </>
  );

  if (botType === "mid_price_brush") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sizeRangeFields()}
        {numField("Interval (seconds)", "interval_seconds")}
        {selectField("Price Mode", "price_mode", [{ value: "normal", label: "Normal" }, { value: "safe", label: "Safe" }])}
        {selectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
      </div>
    );
  }
  if (botType === "natural_brush") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sizeRangeFields()}
        {numField("Interval (seconds)", "interval_seconds")}
        {selectField("Order Type", "order_type", [{ value: "random", label: "Random" }, { value: "buy_first", label: "Buy First" }, { value: "sell_first", label: "Sell First" }])}
        {selectField("Price Mode", "price_mode", [{ value: "normal", label: "Normal" }, { value: "safe", label: "Safe" }])}
        {selectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
      </div>
    );
  }
  if (botType === "oscillation") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sizeRangeFields()}
        {numField("Max Cost (USDT)", "max_cost_usdt")}
        {numField("Total Time (min)", "total_time_minutes")}
        {numField("Oscillate Range %", "oscillate_range_pct", 0.1)}
        {numField("Oscillate Ratio %", "oscillate_ratio_pct", 0.1)}
        {selectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
      </div>
    );
  }
  if (botType === "range_oscillation") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sizeRangeFields()}
        {numField("Min Price", "min_price", 0.00000001)}
        {numField("Max Price", "max_price", 0.00000001)}
        {numField("Min Interval (seconds)", "min_interval")}
        {numField("Max Interval (seconds)", "max_interval")}
        {numField("Max Move Per Trade %", "max_move_per_trade_pct", 0.05)}
        {numField("Consolidation Chance %", "consolidation_chance_pct", 1)}
        {selectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
        <div className="col-span-2 text-xs text-muted-foreground">
          🛡️ <strong>Max Move Per Trade %</strong>: caps how far each order can deviate from current price (default 0.3%). Prevents dump-wicks.<br/>
          🛡️ <strong>Anti-wick</strong>: SELL orders never cross the bid, BUY orders never cross the ask → always passive maker orders.<br/>
          🎲 <strong>Consolidation Chance %</strong>: random pause probability for organic flat patches.
        </div>
      </div>
    );
  }
  if (botType === "depth_spread") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {depthSizeFields()}
        {numField("Buy Sheets (Layers)", "buy_sheet_count")}
        {numField("Sell Sheets (Layers)", "sell_sheet_count")}
        {numField("Auto-Refresh Interval (sec)", "duration_seconds")}
        {numField("Buy Spread Min % (0 = from 0%)", "buy_spread_min_pct", 0.1)}
        {numField("Buy Spread Max %", "buy_spread_pct", 0.1)}
        {numField("Sell Spread Min % (0 = from 0%)", "sell_spread_min_pct", 0.1)}
        {numField("Sell Spread Max %", "sell_spread_pct", 0.1)}
        {numField("Layer Step % (0 = auto)", "layer_step_pct", 0.05)}
        {numField("Max Spread Tolerance % (0 = off)", "max_spread_tolerance_pct", 0.1)}
        {numField("Dump Protection % (0 = off)", "dump_protection_pct", 0.1)}
        {numField("Dump Pause (seconds)", "dump_pause_seconds")}
        {selectField("Price Mode", "price_mode", [{ value: "current", label: "Current Price" }, { value: "pricing", label: "Fixed Price" }])}
        {config?.price_mode === "pricing" && numField("Fixed Price", "pricing_value", 0.00000001)}
        {config?.price_mode === "current" && numField("Sell Price Floor (0 = off)", "sell_price_floor", 0.00000001)}
        {config?.price_mode === "current" && numField("Buy Price Ceiling (0 = off)", "buy_price_ceiling", 0.00000001)}
        {selectField("Order Type", "order_type", [{ value: "normal", label: "Normal" }, { value: "safe", label: "Safe" }])}
        {selectField("Distribution", "distribution_type", [{ value: "longtail", label: "Long-tail" }, { value: "random", label: "Random" }])}
        <div className="col-span-2 text-xs text-muted-foreground">
          ♻️ <strong>Always refreshes every interval</strong>: cancels all bot orders & re-places at current price. No more stuck-on-dump bug.<br/>
          🛡️ <strong>Dump Protection %</strong>: if price drops more than this since last reset → cancels orders, pauses, then <em>force-resets</em> at new price. <strong>0 = disabled</strong> (recommended unless you really need it — old version with hardcoded 0.3% caused freezing).<br/>
          💡 <strong>Spread Min/Max %</strong>: orders distribute evenly between Min% and Max%. <strong>Layer Step %</strong>: fixed distance per layer (overrides auto). <strong>Max Spread Tolerance</strong>: skip placement if orderbook spread too wide.<br/>
          🧱 <strong>Sell Price Floor / Buy Price Ceiling</strong> (Current Price mode only): if a layer&apos;s computed price would cross the limit, it&apos;s clamped to that price. <strong>0 = off</strong>.
        </div>
      </div>
    );
  }
  if (botType === "depth_near") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {depthSizeFields()}
        {numField("Buy Sheets", "buy_sheet_count")}
        {numField("Sell Sheets", "sell_sheet_count")}
        {numField("Duration (seconds)", "duration_seconds")}
        {numField("Buy Spread Min %", "buy_spread_min_pct", 0.1)}
        {numField("Buy Spread Max %", "buy_spread_max_pct", 0.1)}
        {numField("Sell Spread Min %", "sell_spread_min_pct", 0.1)}
        {numField("Sell Spread Max %", "sell_spread_max_pct", 0.1)}
        {selectField("Price Mode", "price_mode", [{ value: "current", label: "Current Price" }, { value: "pricing", label: "Fixed Price" }])}
        {config?.price_mode === "pricing" && numField("Fixed Price", "pricing_value", 0.00000001)}
        {config?.price_mode === "current" && numField("Sell Price Floor (0 = off)", "sell_price_floor", 0.00000001)}
        {config?.price_mode === "current" && numField("Buy Price Ceiling (0 = off)", "buy_price_ceiling", 0.00000001)}
        {selectField("Order Type", "order_type", [{ value: "normal", label: "Normal" }, { value: "safe", label: "Safe" }])}
        {selectField("Distribution", "distribution_type", [{ value: "longtail", label: "Long-tail" }, { value: "random", label: "Random" }])}
      </div>
    );
  }
  if (botType === "depth_remote") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {numField("Buy Min Size", "buy_min_order_size")}
        {numField("Buy Max Size", "buy_max_order_size")}
        {numField("Number of Orders", "sheet_count")}
        {numField("Start Spread % (nearer)", "start_spread_pct", 0.1)}
        {numField("End Spread % (farther)", "end_spread_pct", 0.1)}
        {numField("Refresh Interval (sec)", "duration_seconds")}
        {selectField("Price Mode", "price_mode", [{ value: "current", label: "Current Price" }, { value: "pricing", label: "Fixed Price" }])}
        {config?.price_mode === "pricing" && numField("Fixed Price (anchor)", "pricing_value", 0.00000001)}
        {config?.price_mode === "current" && numField("Buy Price Ceiling (0 = off)", "buy_price_ceiling", 0.00000001)}
        <div className="col-span-2 text-xs text-muted-foreground">
          💡 Places <strong>BUY-only</strong> orders evenly between <strong>-Start%</strong> and <strong>-End%</strong> below anchor price (current or fixed).<br/>
          Example with Fixed Price = 0.005, Start=20, End=25 → N buys between 0.00375 and 0.00400.<br/>
          🧱 <strong>Buy Price Ceiling</strong> (Current Price mode only): clamps any BUY computed above this price. <strong>0 = off</strong>.
        </div>
      </div>
    );
  }

  if (botType === "best_oscillation") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sizeRangeFields()}
        {numField("Min Price (optional)", "min_price", 0.00000001)}
        {numField("Max Price (optional)", "max_price", 0.00000001)}
        {numField("Interval Min (sec)", "interval_min_seconds")}
        {numField("Interval Max (sec)", "interval_max_seconds")}
        {numField("Oscillation Range %", "oscillation_range_pct", 0.1)}
        {numField("Spread % (buy↔sell)", "spread_pct", 0.05)}
        {numField("Anti-Dump Protection %", "anti_dump_protection_pct", 0.1)}
        {numField("Dump Threshold %", "dump_threshold_pct", 0.1)}
        {numField("Dump Pause (seconds)", "dump_pause_seconds")}
        <div className="col-span-2 text-xs text-muted-foreground">
          💡 <strong>Spread %</strong>: distance between BUY/SELL prices around current (e.g. 0.2 = ±0.1%).<br/>
          💡 <strong>Dump Threshold %</strong>: if price drops more than this from recent peak, bot CANCELS ALL orders & pauses for <strong>Dump Pause</strong> seconds.<br/>
          💡 Leave Min/Max Price as 0 to use percentage-based oscillation. Set both to use fixed price range.
        </div>
      </div>
    );
  }
  if (botType === "gradual_lift") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {numField("Target Price", "target_price", 0.00000001)}
        {sizeRangeFields()}
        {numField("Max Cost (USDT)", "max_cost_usdt")}
        {numField("Total Time (min)", "total_time_minutes")}
        {numField("Oscillate Range %", "oscillate_range_pct", 0.1)}
        {numField("Consolidation Chance % (organic pauses)", "consolidation_chance_pct", 1)}
        {selectField("Order Type", "order_type", [
          { value: "LIMIT", label: "Limit Orders (2-min auto-cancel)" }, 
          { value: "MARKET", label: "Market Orders (instant fill)" }
        ])}
        {selectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
        <div className="col-span-2 text-xs text-muted-foreground">
          🛡️ <strong>Anti-Dump</strong>: BUY-only. Price floor = highest price seen. No SELL orders that could drag price down.<br/>
          🎲 <strong>Consolidation Chance</strong>: % chance per cycle to skip trade + pause longer (organic-looking flat patches).<br/>
          📈 Upward-only oscillation overlay: chart wiggles UP, never DOWN.
        </div>
      </div>
    );
  }
  if (botType === "push") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {numField("Target Price", "target_price", 0.00000001)}
        {sizeRangeFields()}
        {numField("Interval (sec)", "interval_seconds")}
        {numField("Max Duration (min)", "max_duration_minutes")}
        {numField("Max Budget (USDT, 0 = unlimited)", "max_budget_usdt")}
        {selectField("Order Type", "order_type", [
          { value: "LIMIT", label: "Limit Orders (2-min auto-cancel)" }, 
          { value: "MARKET", label: "Market Orders (instant fill)" }
        ])}
        <div className="col-span-2 text-xs text-muted-foreground">
          💡 Bot stops when target price OR budget is reached (whichever first). Budget 0 = no cap.
        </div>
      </div>
    );
  }
  if (botType === "order_flash") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sizeRangeFields()}
        {selectField("Side", "side", [
          { value: "both", label: "Both (alternate BUY/SELL)" },
          { value: "buy", label: "Buy only" },
          { value: "sell", label: "Sell only" },
          { value: "random", label: "Random" },
        ])}
        {numField("Spread % (from current price)", "spread_pct", 0.05)}
        {numField("Flash Duration (sec)", "flash_duration_seconds", 0.5)}
        {numField("Interval (sec, between flashes)", "interval_seconds", 0.5)}
        <div className="col-span-2 text-xs text-muted-foreground">
          ⚡ <strong>Order Flash</strong>: places a LIMIT order at <strong>Spread %</strong> from price, holds it for <strong>Flash Duration</strong> seconds, then cancels.<br/>
          Creates a flashing depth signal on the orderbook. If an order fills during the hold, it&apos;s logged as &quot;filled&quot; instead of &quot;cancelled&quot;.
        </div>
      </div>
    );
  }

  if (botType === "cross_account") {
    return (
      <div className="space-y-4">
        {/* Account 2 key selector */}
        <div className="p-3 rounded-md bg-primary/5 border border-primary/20">
          <div className="flex items-center gap-2 mb-2">
            <ArrowLeftRight className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">Cross-Account Setup</span>
          </div>
          <p className="text-[10px] text-muted-foreground mb-3">Account 1 (primary key above) and Account 2 below will trade against each other. Orders from one account fill the other — no self-trade.</p>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider">Account 2 API Key</Label>
            <Select value={apiKeyId2 || ""} onValueChange={(v) => setApiKeyId2(v)}>
              <SelectTrigger className="bg-secondary/50" data-testid="bot-key2-select"><SelectValue placeholder="Select Account 2 Key" /></SelectTrigger>
              <SelectContent>
                {keys.map((k) => <SelectItem key={k.id} value={k.id}>{k.label} ({k.api_key_masked})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Separator />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sizeRangeFields()}
          {numField("Interval (seconds)", "interval_seconds")}
          {selectField("Buy Order Type", "buy_order_type", [
            { value: "LIMIT", label: "Limit (preferred, no slippage)" },
            { value: "MARKET", label: "Market (instant fill)" },
          ])}
          {selectField("Price Anchor", "price_anchor", [
            { value: "ask", label: "Best Ask (no dump — recommended)" },
            { value: "mid", label: "Mid Price (legacy)" },
            { value: "bid", label: "Best Bid (cheap fills)" },
          ])}
          {numField("Price Offset % (above anchor)", "price_offset_pct", 0.005)}
          {numField("🎯 Target Volume (USDT, 0 = unlimited)", "target_volume_usdt")}
          {numField("Max Wait for Fill (seconds)", "max_wait_seconds")}
          {numField("Poll Interval (seconds)", "poll_interval_seconds", 0.5)}
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <p>💡 <strong>Flow:</strong> SELL LIMIT (Acct1 first, inside spread) → BUY ({config?.buy_order_type || "LIMIT"}) at same price → matches only your SELL (external orders untouched).</p>
          <p>🔁 <strong>Alternates:</strong> Round 1: Acct1 sells → Acct2 buys · Round 2: Acct2 sells → Acct1 buys · repeat.</p>
          <p>🛡️ <strong>Anchor:</strong> &quot;Ask&quot; places just BELOW best ask (becomes new best ask, no dump). &quot;Bid&quot; places just ABOVE best bid (cheap fills). &quot;Mid&quot; uses (bid+ask)/2.</p>
          <p>⚡ <strong>Fast fill-check:</strong> Polls each exact order by ID every <strong>Poll Interval</strong> (default 0.2s). Interval is the *total* round cadence — extra waiting only added if fills are slower than your interval.</p>
          <p>💰 <strong>$10 → $10K volume:</strong> Assets recycle between accounts each round. With $10 capital + 2s interval, ~1000 trades ≈ $10,000 volume.</p>
        </div>
      </div>
    );
  }
  return null;
}

export default function BotsPage() {
  const [bots, setBots] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [apiKeyId2, setApiKeyId2] = useState("");
  const [newBot, setNewBot] = useState({
    name: "", bot_type: "natural_brush", symbol: "NOMOXUSDT",
    api_key_id: "", api_key_id_2: null,
    config: { ...DEFAULT_CONFIGS.natural_brush }
  });

  const fetchBots = useCallback(async () => {
    try {
      const [bRes, kRes] = await Promise.all([api.get("/bots"), api.get("/keys")]);
      setBots(bRes.data);
      setKeys(kRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBots(); const iv = setInterval(fetchBots, 10000); return () => clearInterval(iv); }, [fetchBots]);

  const createBot = async () => {
    if (!newBot.api_key_id) { toast.error("Select an API key"); return; }
    if (newBot.bot_type === "cross_account" && !apiKeyId2) { toast.error("Select Account 2 API key for cross-account bot"); return; }
    if (newBot.bot_type === "cross_account" && newBot.api_key_id === apiKeyId2) { toast.error("Account 1 and Account 2 must use different API keys"); return; }
    const payload = {
      ...newBot,
      api_key_id_2: newBot.bot_type === "cross_account" ? apiKeyId2 : null,
    };
    try {
      await api.post("/bots", payload);
      toast.success("Bot created");
      setDialogOpen(false);
      setApiKeyId2("");
      fetchBots();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create bot"); }
  };

  const openEditDialog = (bot) => {
    setEditingBot({
      id: bot.id,
      name: bot.name,
      config: { ...bot.config }
    });
    setEditDialogOpen(true);
  };

  const updateBot = async () => {
    if (!editingBot.name.trim()) {
      toast.error("Bot name is required");
      return;
    }
    try {
      await api.put(`/bots/${editingBot.id}`, {
        name: editingBot.name,
        config: editingBot.config
      });
      toast.success("Bot updated successfully");
      setEditDialogOpen(false);
      setEditingBot(null);
      fetchBots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update bot");
    }
  };

  const startBot = async (id) => {
    try { await api.post(`/bots/${id}/start`); toast.success("Bot started"); fetchBots(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
  };
  const stopBot = async (id) => {
    try { await api.post(`/bots/${id}/stop`); toast.success("Bot stopped"); fetchBots(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to stop"); }
  };
  const deleteBot = async (id) => {
    try { await api.delete(`/bots/${id}`); toast.success("Bot deleted"); fetchBots(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const typeLabel = (t) => BOT_TYPES.find((b) => b.value === t)?.label || t;
  const typeIcon = (t) => {
    if (t === "cross_account") return <ArrowLeftRight className="w-3.5 h-3.5 text-primary" />;
    if (t === "range_oscillation") return <Waves className="w-3.5 h-3.5 text-amber-400" />;
    return null;
  };

  // Render edit config fields based on current bot
  const renderEditConfigFields = () => {
    if (!editingBot) return null;

    // Find the bot type from existing bots
    const bot = bots.find(b => b.id === editingBot.id);
    if (!bot) return null;

    const botType = bot.bot_type;

    // Helper functions for editing
    const editNumField = (label, key, step = 1) => (
      <div>
        <Label>{label}</Label>
        <Input
          type="number"
          step={step}
          value={editingBot.config[key] || ""}
          onChange={(e) => setEditingBot({
            ...editingBot,
            config: { ...editingBot.config, [key]: parseFloat(e.target.value) || 0 }
          })}
        />
      </div>
    );

    const editSelectField = (label, key, options) => (
      <div>
        <Label>{label}</Label>
        <Select
          value={editingBot.config[key]}
          onValueChange={(v) => setEditingBot({
            ...editingBot,
            config: { ...editingBot.config, [key]: v }
          })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );

    const editSizeRangeFields = () => (
      <>
        {editNumField("Min Order Size", "min_order_size")}
        {editNumField("Max Order Size", "max_order_size")}
      </>
    );

    const editDepthSizeFields = () => (
      <>
        {editNumField("Buy Min Size", "buy_min_order_size")}
        {editNumField("Buy Max Size", "buy_max_order_size")}
        {editNumField("Sell Min Size", "sell_min_order_size")}
        {editNumField("Sell Max Size", "sell_max_order_size")}
      </>
    );

    // Render fields based on bot type
    if (botType === "mid_price_brush") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Interval (seconds)", "interval_seconds")}
          {editSelectField("Price Mode", "price_mode", [{ value: "normal", label: "Normal" }, { value: "safe", label: "Safe" }])}
          {editSelectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
        </div>
      );
    }
    if (botType === "natural_brush") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Interval (seconds)", "interval_seconds")}
          {editSelectField("Order Type", "order_type", [{ value: "random", label: "Random" }, { value: "buy_first", label: "Buy First" }, { value: "sell_first", label: "Sell First" }])}
          {editSelectField("Price Mode", "price_mode", [{ value: "normal", label: "Normal" }, { value: "safe", label: "Safe" }])}
          {editSelectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
        </div>
      );
    }
    if (botType === "oscillation") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Max Cost (USDT)", "max_cost_usdt")}
          {editNumField("Total Time (min)", "total_time_minutes")}
          {editNumField("Oscillate Range %", "oscillate_range_pct", 0.1)}
          {editNumField("Oscillate Ratio %", "oscillate_ratio_pct", 0.1)}
          {editSelectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
        </div>
      );
    }
    if (botType === "range_oscillation") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Min Price", "min_price", 0.00000001)}
          {editNumField("Max Price", "max_price", 0.00000001)}
          {editNumField("Min Interval (seconds)", "min_interval")}
          {editNumField("Max Interval (seconds)", "max_interval")}
          {editNumField("Max Move Per Trade %", "max_move_per_trade_pct", 0.05)}
          {editNumField("Consolidation Chance %", "consolidation_chance_pct", 1)}
          {editSelectField("Distribution", "distribution_type", [{ value: "continuous", label: "Continuous" }, { value: "random", label: "Random" }])}
        </div>
      );
    }
    if (botType === "depth_spread" || botType === "depth_near") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editDepthSizeFields()}
          {editNumField("Buy Sheets (Layers)", "buy_sheet_count")}
          {editNumField("Sell Sheets (Layers)", "sell_sheet_count")}
          {editNumField("Auto-Refresh Interval (sec)", "duration_seconds")}
          {botType === "depth_spread" && editNumField("Buy Spread Min % (0 = from 0%)", "buy_spread_min_pct", 0.1)}
          {editNumField("Buy Spread Max %", "buy_spread_pct", 0.1)}
          {botType === "depth_spread" && editNumField("Sell Spread Min % (0 = from 0%)", "sell_spread_min_pct", 0.1)}
          {editNumField("Sell Spread Max %", "sell_spread_pct", 0.1)}
          {botType === "depth_spread" && editNumField("Layer Step % (0 = auto)", "layer_step_pct", 0.05)}
          {botType === "depth_spread" && editNumField("Max Spread Tolerance % (0 = off)", "max_spread_tolerance_pct", 0.1)}
          {botType === "depth_spread" && editNumField("Dump Protection % (0 = off)", "dump_protection_pct", 0.1)}
          {botType === "depth_spread" && editNumField("Dump Pause (seconds)", "dump_pause_seconds")}
          {editSelectField("Price Mode", "price_mode", [{ value: "current", label: "Current Price" }, { value: "pricing", label: "Fixed Price" }])}
          {editingBot.config?.price_mode === "pricing" && editNumField("Fixed Price", "pricing_value", 0.00000001)}
          {editingBot.config?.price_mode === "current" && editNumField("Sell Price Floor (0 = off)", "sell_price_floor", 0.00000001)}
          {editingBot.config?.price_mode === "current" && editNumField("Buy Price Ceiling (0 = off)", "buy_price_ceiling", 0.00000001)}
        </div>
      );
    }
    if (botType === "depth_remote") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editNumField("Buy Min Size", "buy_min_order_size")}
          {editNumField("Buy Max Size", "buy_max_order_size")}
          {editNumField("Number of Orders", "sheet_count")}
          {editNumField("Start Spread % (nearer)", "start_spread_pct", 0.1)}
          {editNumField("End Spread % (farther)", "end_spread_pct", 0.1)}
          {editNumField("Refresh Interval (sec)", "duration_seconds")}
          {editSelectField("Price Mode", "price_mode", [{ value: "current", label: "Current Price" }, { value: "pricing", label: "Fixed Price" }])}
          {editingBot.config?.price_mode === "pricing" && editNumField("Fixed Price (anchor)", "pricing_value", 0.00000001)}
          {editingBot.config?.price_mode === "current" && editNumField("Buy Price Ceiling (0 = off)", "buy_price_ceiling", 0.00000001)}
        </div>
      );
    }

    if (botType === "best_oscillation") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Min Price (optional)", "min_price", 0.00000001)}
          {editNumField("Max Price (optional)", "max_price", 0.00000001)}
          {editNumField("Interval Min (sec)", "interval_min_seconds")}
          {editNumField("Interval Max (sec)", "interval_max_seconds")}
          {editNumField("Oscillation Range %", "oscillation_range_pct", 0.1)}
          {editNumField("Spread % (buy↔sell)", "spread_pct", 0.05)}
          {editNumField("Anti-Dump Protection %", "anti_dump_protection_pct", 0.1)}
          {editNumField("Dump Threshold %", "dump_threshold_pct", 0.1)}
          {editNumField("Dump Pause (seconds)", "dump_pause_seconds")}
        </div>
      );
    }
    if (botType === "gradual_lift") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Target Price", "target_price", 0.00000001)}
          {editNumField("Total Time (min)", "total_time_minutes")}
          {editNumField("Oscillate Range %", "oscillate_range_pct", 0.1)}
          {editNumField("Consolidation Chance %", "consolidation_chance_pct", 1)}
          {editSelectField("Order Type", "order_type", [
            { value: "LIMIT", label: "Limit Orders" }, 
            { value: "MARKET", label: "Market Orders" }
          ])}
        </div>
      );
    }
    if (botType === "push") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Target Price", "target_price", 0.00000001)}
          {editNumField("Interval (sec)", "interval_seconds")}
          {editNumField("Max Duration (min)", "max_duration_minutes")}
          {editNumField("Max Budget (USDT, 0 = unlimited)", "max_budget_usdt")}
          {editSelectField("Order Type", "order_type", [
            { value: "LIMIT", label: "Limit Orders" }, 
            { value: "MARKET", label: "Market Orders" }
          ])}
        </div>
      );
    }
    if (botType === "order_flash") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editSelectField("Side", "side", [
            { value: "both", label: "Both (alternate)" },
            { value: "buy", label: "Buy only" },
            { value: "sell", label: "Sell only" },
            { value: "random", label: "Random" },
          ])}
          {editNumField("Spread %", "spread_pct", 0.05)}
          {editNumField("Flash Duration (sec)", "flash_duration_seconds", 0.5)}
          {editNumField("Interval (sec)", "interval_seconds", 0.5)}
        </div>
      );
    }

    if (botType === "cross_account") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editSizeRangeFields()}
          {editNumField("Interval (seconds)", "interval_seconds")}
          {editSelectField("Buy Order Type", "buy_order_type", [
            { value: "LIMIT", label: "Limit (preferred)" },
            { value: "MARKET", label: "Market" },
          ])}
          {editSelectField("Price Anchor", "price_anchor", [
            { value: "ask", label: "Best Ask (no dump)" },
            { value: "mid", label: "Mid Price (legacy)" },
            { value: "bid", label: "Best Bid" },
          ])}
          {editNumField("Price Offset %", "price_offset_pct", 0.005)}
          {editNumField("Target Volume USDT (0 = unlimited)", "target_volume_usdt")}
          {editNumField("Max Wait for Fill (sec)", "max_wait_seconds")}
          {editNumField("Poll Interval (sec)", "poll_interval_seconds", 0.5)}
        </div>
      );
    }

    return <p className="text-sm text-muted-foreground">No editable configuration for this bot type.</p>;
  };

  return (
    <div className="space-y-6" data-testid="bots-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bot Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Configure and control your market making bots</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-bot-btn" className="w-full sm:w-auto"><Plus className="w-4 h-4 mr-2" />New Bot</Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Bot</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider">Bot Name</Label>
                  <Input value={newBot.name} onChange={(e) => setNewBot((p) => ({ ...p, name: e.target.value }))} placeholder="My Brush Bot" className="bg-secondary/50" data-testid="bot-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider">Symbol</Label>
                  <Input value={newBot.symbol} onChange={(e) => setNewBot((p) => ({ ...p, symbol: e.target.value }))} className="bg-secondary/50 font-mono" data-testid="bot-symbol-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider">Bot Type</Label>
                  <Select value={newBot.bot_type} onValueChange={(v) => { setNewBot((p) => ({ ...p, bot_type: v, config: { ...DEFAULT_CONFIGS[v] } })); setApiKeyId2(""); }}>
                    <SelectTrigger className="bg-secondary/50" data-testid="bot-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BOT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">{BOT_TYPES.find((b) => b.value === newBot.bot_type)?.desc}</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider">
                    {newBot.bot_type === "cross_account" ? "Account 1 API Key" : "API Key"}
                  </Label>
                  <Select value={newBot.api_key_id} onValueChange={(v) => setNewBot((p) => ({ ...p, api_key_id: v }))}>
                    <SelectTrigger className="bg-secondary/50" data-testid="bot-key-select"><SelectValue placeholder="Select API Key" /></SelectTrigger>
                    <SelectContent>
                      {keys.map((k) => <SelectItem key={k.id} value={k.id}>{k.label} ({k.api_key_masked})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-t border-border/50 pt-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">Strategy Configuration</h3>
                <ConfigFields
                  botType={newBot.bot_type}
                  config={newBot.config}
                  setConfig={(fn) => setNewBot((p) => ({ ...p, config: typeof fn === "function" ? fn(p.config) : fn }))}
                  keys={keys}
                  apiKeyId2={apiKeyId2}
                  setApiKeyId2={setApiKeyId2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-create-bot">Cancel</Button>
                <Button onClick={createBot} data-testid="submit-create-bot">Create Bot</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Bot Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Bot</DialogTitle>
          </DialogHeader>
          {editingBot && (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Bot Name</Label>
                <Input
                  value={editingBot.name}
                  onChange={(e) => setEditingBot({ ...editingBot, name: e.target.value })}
                  placeholder="My Bot"
                  data-testid="edit-bot-name"
                />
              </div>

              <Separator />

              <div>
                <Label className="text-base font-semibold">Configuration</Label>
                <p className="text-xs text-muted-foreground mb-3">Update bot settings (bot type and API keys cannot be changed)</p>
                {renderEditConfigFields()}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="cancel-edit-bot">Cancel</Button>
                <Button onClick={updateBot} data-testid="submit-edit-bot">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bot Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48 rounded-lg" />)}</div>
      ) : bots.length === 0 ? (
        <Card className="bg-card border-border/50 py-16 text-center">
          <Bot className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">No bots yet. Create your first bot to start market making.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bots.map((bot) => (
            <Card key={bot.id} className={`bg-card border-border/50 transition-all duration-200 ${bot.status === "running" ? "border-emerald-500/30" : ""}`} data-testid={`bot-card-${bot.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {typeIcon(bot.bot_type)}
                    <CardTitle className="text-sm font-semibold truncate">{bot.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <CircleDot className={`w-3 h-3 ${bot.status === "running" ? "text-emerald-500" : "text-muted-foreground"}`} />
                    <Badge variant={bot.status === "running" ? "default" : "secondary"} className={`text-[10px] ${bot.status === "running" ? "bg-emerald-500/15 text-emerald-500 border-transparent" : ""}`}>{bot.status}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium">{typeLabel(bot.bot_type)}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Symbol:</span>
                  <span className="font-mono font-medium">{bot.symbol}</span>
                </div>
                {bot.config && (bot.config.min_order_size || bot.config.buy_min_order_size) && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Size:</span>
                    <span className="font-mono text-primary">
                      {bot.config.min_order_size || bot.config.buy_min_order_size} - {bot.config.max_order_size || bot.config.buy_max_order_size}
                    </span>
                  </div>
                )}
                {bot.bot_type === "cross_account" && (
                  <Badge className="bg-primary/15 text-primary border-transparent text-[10px]">
                    <ArrowLeftRight className="w-3 h-3 mr-1" />2 Accounts
                  </Badge>
                )}
                <div className="flex gap-2 pt-2">
                  {bot.status === "running" ? (
                    <Button size="sm" variant="destructive" onClick={() => stopBot(bot.id)} className="flex-1" data-testid={`stop-bot-${bot.id}`}>
                      <Square className="w-3 h-3 mr-1" />Stop
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => startBot(bot.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`start-bot-${bot.id}`}>
                      <Play className="w-3 h-3 mr-1" />Start
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => openEditDialog(bot)} 
                    disabled={bot.status === "running"}
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10" 
                    data-testid={`edit-bot-${bot.id}`}
                  >
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteBot(bot.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-bot-${bot.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
